import type { ChangeEvent, FC, FormEvent, ReactNode } from 'react'
import type { AppUser } from '@/types'

import { useState } from 'react'
import { Seo } from '@/components/generals/seo'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Card } from '@/components/ui/card'
import { Dialog } from '@/components/ui/dialog'
import { Avatar } from '@/components/ui/feedback'
import { Field, Label } from '@/components/ui/field'
import { Input } from '@/components/ui/input'
import { Switch } from '@/components/ui/switch'
import { BrandMark } from '@/components/app/brand-mark'
import { SearchField } from '@/components/app/search-field'
import { useAcademic } from '@/components/providers/academic-provider'
import { useConfirm } from '@/components/providers/confirm-provider'
import { useToast } from '@/components/providers/toast-provider'
import { MAX_LOGO_SIZE, MIN_LOGO_SIZE } from '@/constants/academic'

import {
    THead,
    TBody,
    TD,
    TH,
    TR,
    Table
} from '@/components/ui/table'

import {
    ImageUp,
    Pencil,
    Plus,
    Save,
    Settings,
    Trash2,
    UserCog,
    UserPlus
} from 'lucide-react'

const LOGO_TYPES = ['image/png', 'image/jpeg', 'image/webp']

const BrandingSection: FC = (): ReactNode => {
    const { data, actions } = useAcademic()
    const confirm = useConfirm()
    const toast = useToast()
    const { settings } = data

    const [appName, setAppName] = useState(settings.appName)
    const [tagline, setTagline] = useState(settings.tagline)
    const [logoSize, setLogoSize] = useState(settings.logoSize)
    const [showLogo, setShowLogo] = useState(settings.showLogo)
    const [pendingLogo, setPendingLogo] = useState<string | null>(null)
    const [fileKey, setFileKey] = useState(0)

    const previewLogo = pendingLogo ?? settings.logoUrl

    const handleLogo = (event: ChangeEvent<HTMLInputElement>): void => {
        const file = event.target.files?.[0]

        if (!file) {
            setPendingLogo(null)

            return
        }

        if (!LOGO_TYPES.includes(file.type) || file.size > 3 * 1024 * 1024) {
            toast.error('Logo tidak dapat dipakai', 'Gunakan file PNG, JPG, atau WebP berukuran maksimal 3 MB.')
            setFileKey(key => key + 1)

            return
        }

        const reader = new FileReader()

        reader.onload = () => setPendingLogo(typeof reader.result === 'string' ? reader.result : null)
        reader.readAsDataURL(file)
    }

    const handleSubmit = (event: FormEvent<HTMLFormElement>): void => {
        event.preventDefault()

        const result = actions.saveBranding({
            appName,
            tagline,
            logoSize,
            showLogo,
            logoUrl: pendingLogo ?? undefined
        })

        if (result.ok) {
            toast.success(result.message ?? 'Tampilan aplikasi berhasil disimpan.')
            setPendingLogo(null)
            setFileKey(key => key + 1)
        }
        else {
            toast.error('Gagal menyimpan', result.error)
        }
    }

    const handleRemoveLogo = async (): Promise<void> => {
        if (!await confirm({ title: 'Hapus logo kustom?', description: 'Ikon akan kembali memakai huruf pertama nama aplikasi.', confirmLabel: 'Hapus logo' })) return

        const result = actions.removeLogo()

        if (result.ok) toast.success(result.message ?? 'Logo kustom berhasil dihapus.')
        setPendingLogo(null)
        setFileKey(key => key + 1)
    }

    return (
        <Card id='branding' className='mb-6'>
            <header className='flex items-start justify-between gap-4 border-b px-5 py-4'>
                <div>
                    <p className='text-[11px] font-semibold tracking-[0.12em] text-primary uppercase'>
                        Tampilan aplikasi
                    </p>

                    <h2 className='mt-0.5 text-[15px] font-semibold text-foreground'>
                        Branding dan logo
                    </h2>

                    <p className='mt-0.5 text-[13px] text-muted-foreground'>
                        Atur nama yang terlihat di menu samping dan tampilkan atau sembunyikan ikon logo.
                    </p>
                </div>

                <span className='text-2xl font-bold text-muted-foreground/40 tabular-nums' aria-hidden='true'>
                    01
                </span>
            </header>

            <div className='grid gap-6 p-5 lg:grid-cols-[minmax(0,1fr)_minmax(0,22rem)]'>
                <form onSubmit={handleSubmit} className='space-y-5'>
                    <Field label='Nama aplikasi' htmlFor='appName' hint='Ditampilkan sebagai judul brand pada menu samping.'>
                        <Input id='appName' value={appName} onChange={event => setAppName(event.target.value)} maxLength={60} required />
                    </Field>

                    <Field label='Deskripsi singkat' htmlFor='appTagline'>
                        <Input id='appTagline' value={tagline} onChange={event => setTagline(event.target.value)} maxLength={90} placeholder='Contoh: Portal Akademik' />
                    </Field>

                    <Field label='Gambar logo' htmlFor='logoFile' hint='PNG, JPG, WebP · maksimal 3 MB.'>
                        <Input key={fileKey} id='logoFile' type='file' accept='image/png,image/jpeg,image/webp' onChange={handleLogo} />
                    </Field>

                    {settings.logoUrl && (
                        <div className='-mt-2 flex flex-wrap items-center gap-2'>
                            <span className='text-xs text-muted-foreground'>Logo kustom sedang digunakan.</span>

                            <Button variant='danger-outline' size='xs' onClick={() => void handleRemoveLogo()}>
                                <Trash2 aria-hidden='true' />
                                Hapus logo
                            </Button>
                        </div>
                    )}

                    <Field
                        label='Ukuran logo di menu'
                        htmlFor='logoSize'
                        hint='Ukuran logo pada menu samping dan halaman masuk.'
                        trailing={(
                            <output htmlFor='logoSize' className='rounded-md bg-muted px-2 py-0.5 text-xs font-semibold text-foreground tabular-nums'>
                                {logoSize} px
                            </output>
                        )}
                    >
                        <input
                            id='logoSize'
                            type='range'
                            min={MIN_LOGO_SIZE}
                            max={MAX_LOGO_SIZE}
                            step={2}
                            value={logoSize}
                            onChange={event => setLogoSize(Number(event.target.value))}
                            className='h-2 w-full cursor-pointer appearance-auto accent-primary'
                        />
                    </Field>

                    <div className='flex items-center gap-3'>
                        <Switch id='showLogo' checked={showLogo} onCheckedChange={setShowLogo} aria-labelledby='showLogoLabel' />

                        <Label id='showLogoLabel' htmlFor='showLogo' className='cursor-pointer'>
                            Tampilkan ikon logo
                        </Label>
                    </div>

                    <Button type='submit'>
                        <Save aria-hidden='true' />
                        Simpan tampilan
                    </Button>
                </form>

                <div className='flex flex-col rounded-xl border bg-muted/30 p-5'>
                    <p className='text-[11px] font-semibold tracking-[0.12em] text-muted-foreground uppercase'>
                        Pratinjau menu
                    </p>

                    <div className='mt-5 flex flex-1 items-center justify-center'>
                        <div className='flex w-full max-w-xs items-center gap-3 rounded-xl border bg-sidebar p-4 shadow-card'>
                            {showLogo && (
                                <BrandMark size={logoSize} settings={{ appName: appName || 'A', logoUrl: previewLogo, logoSize }} />
                            )}

                            <span className='min-w-0'>
                                <strong className='block truncate text-[15px] font-bold text-foreground'>
                                    {appName || 'Nama aplikasi'}
                                </strong>

                                <span className='block truncate text-xs text-muted-foreground'>
                                    {tagline}
                                </span>
                            </span>
                        </div>
                    </div>

                    <p className='mt-5 flex items-center gap-1.5 text-xs text-muted-foreground'>
                        <ImageUp className='size-3.5' aria-hidden='true' />
                        Perubahan diterapkan ke menu setelah disimpan.
                    </p>
                </div>
            </div>
        </Card>
    )
}

type UserFormState = {
    id: string | null
    name: string
    username: string
    password: string
    active: boolean
}

const EMPTY_USER: UserFormState = { id: null, name: '', username: '', password: '', active: true }

const UsersSection: FC = (): ReactNode => {
    const { users, currentUser, actions } = useAcademic()
    const confirm = useConfirm()
    const toast = useToast()

    const [query, setQuery] = useState('')
    const [open, setOpen] = useState(false)
    const [form, setForm] = useState<UserFormState>(EMPTY_USER)

    const search = query.trim().toLowerCase()
    const rows = users.filter(user => !search || `${user.name} ${user.username}`.toLowerCase().includes(search))
    const editing = form.id !== null

    const openCreate = (): void => {
        setForm(EMPTY_USER)
        setOpen(true)
    }

    const openEdit = (user: AppUser): void => {
        setForm({ id: user.id, name: user.name, username: user.username, password: '', active: user.active })
        setOpen(true)
    }

    const handleSubmit = (event: FormEvent<HTMLFormElement>): void => {
        event.preventDefault()

        const result = actions.saveUser({
            id: form.id ?? undefined,
            name: form.name,
            username: form.username,
            password: form.password,
            active: form.active
        })

        if (result.ok) {
            toast.success(result.message ?? 'Pengguna disimpan.')
            setOpen(false)
        }
        else {
            toast.error('Gagal menyimpan', result.error)
        }
    }

    const handleDelete = async (user: AppUser): Promise<void> => {
        if (!await confirm({ title: `Hapus pengguna ${user.username}?`, description: 'Akun ini tidak dapat lagi masuk ke sistem.', confirmLabel: 'Ya, hapus' })) return

        const result = actions.deleteUser(user.id)

        if (result.ok) toast.success(result.message ?? 'Pengguna dihapus.')
        else toast.error('Gagal menghapus', result.error)
    }

    return (
        <Card id='users'>
            <header className='flex flex-col gap-3 border-b px-5 py-4 sm:flex-row sm:items-center sm:justify-between'>
                <div className='flex items-start gap-3'>
                    <span className='grid size-9 shrink-0 place-items-center rounded-lg bg-primary/10 text-primary' aria-hidden='true'>
                        <UserCog className='size-[18px]' />
                    </span>

                    <div>
                        <h2 className='text-[15px] font-semibold text-foreground'>Pengguna</h2>
                        <p className='text-[13px] text-muted-foreground'>Kelola akun pengguna sistem</p>
                    </div>
                </div>

                <Button onClick={openCreate}>
                    <Plus aria-hidden='true' />
                    Tambah Pengguna
                </Button>
            </header>

            <div className='border-b px-5 py-3'>
                <SearchField
                    label='Cari pengguna'
                    placeholder='Cari nama/username...'
                    value={query}
                    onChange={event => setQuery(event.target.value)}
                    wrapperClassName='max-w-sm'
                />
            </div>

            <Table>
                <THead>
                    <tr>
                        <TH>Nama</TH>
                        <TH>Username</TH>
                        <TH>Status</TH>
                        <TH className='text-right'>Aksi</TH>
                    </tr>
                </THead>

                <TBody>
                    {rows.map(user => (
                        <TR key={user.id}>
                            <TD>
                                <span className='flex items-center gap-3'>
                                    <Avatar name={user.name} className='size-8 text-[11px]' />
                                    <span className='font-semibold text-foreground'>
                                        {user.name}
                                        {user.id === currentUser.id && <span className='ml-1.5 font-normal text-muted-foreground'>(Anda)</span>}
                                    </span>
                                </span>
                            </TD>
                            <TD className='font-mono text-xs text-muted-foreground'>{user.username}</TD>
                            <TD>
                                <Badge tone={user.active ? 'success' : 'neutral'} dot>
                                    {user.active ? 'Aktif' : 'Nonaktif'}
                                </Badge>
                            </TD>
                            <TD>
                                <div className='flex justify-end gap-2'>
                                    <Button variant='ghost' size='xs' className='text-primary' onClick={() => openEdit(user)}>
                                        <Pencil aria-hidden='true' />
                                        Ubah
                                    </Button>

                                    {user.id !== currentUser.id && (
                                        <Button variant='ghost' size='xs' className='text-destructive hover:bg-destructive/10 hover:text-destructive' onClick={() => void handleDelete(user)}>
                                            <Trash2 aria-hidden='true' />
                                            Hapus
                                        </Button>
                                    )}
                                </div>
                            </TD>
                        </TR>
                    ))}

                    {rows.length === 0 && (
                        <tr>
                            <td colSpan={4} className='px-5 py-10 text-center text-sm text-muted-foreground'>
                                {users.length === 0 ? 'Belum ada akun pengguna.' : 'Tidak ada pengguna yang cocok dengan pencarian.'}
                            </td>
                        </tr>
                    )}
                </TBody>
            </Table>

            <Dialog
                open={open}
                onClose={() => setOpen(false)}
                icon={editing ? <UserCog /> : <UserPlus />}
                title={editing ? 'Ubah Pengguna' : 'Tambah Pengguna'}
                footer={(
                    <>
                        <Button variant='outline' onClick={() => setOpen(false)}>
                            Batal
                        </Button>

                        <Button type='submit' form='settingsUserForm'>
                            {editing ? 'Simpan Perubahan' : 'Tambah Pengguna'}
                        </Button>
                    </>
                )}
            >
                <form id='settingsUserForm' onSubmit={handleSubmit} className='space-y-4 pt-1'>
                    <Field label='Nama' htmlFor='settingsUserName'>
                        <Input id='settingsUserName' value={form.name} onChange={event => setForm(current => ({ ...current, name: event.target.value }))} maxLength={100} required />
                    </Field>

                    <Field label='Username' htmlFor='settingsUserUsername'>
                        <Input id='settingsUserUsername' value={form.username} onChange={event => setForm(current => ({ ...current, username: event.target.value }))} maxLength={50} required autoComplete='off' />
                    </Field>

                    <Field
                        label={<>Password {editing && <span className='font-normal text-muted-foreground'>(kosongkan jika tidak diubah)</span>}</>}
                        htmlFor='settingsUserPassword'
                    >
                        <Input
                            id='settingsUserPassword'
                            type='password'
                            value={form.password}
                            onChange={event => setForm(current => ({ ...current, password: event.target.value }))}
                            minLength={6}
                            required={!editing}
                            autoComplete='new-password'
                        />
                    </Field>

                    <label htmlFor='settingsUserActive' className='flex w-fit items-center gap-2.5 text-[13px] font-semibold text-foreground'>
                        <input
                            id='settingsUserActive'
                            type='checkbox'
                            checked={form.active}
                            onChange={event => setForm(current => ({ ...current, active: event.target.checked }))}
                            className='size-4 cursor-pointer rounded accent-primary'
                        />
                        Aktif
                    </label>
                </form>
            </Dialog>
        </Card>
    )
}

const SettingsPage: FC = (): ReactNode => {
    const { currentUser } = useAcademic()

    return (
        <>
            <Seo title='Pengaturan' />

            <div className='mb-6 flex items-center gap-4'>
                <span className='grid size-12 shrink-0 place-items-center rounded-2xl bg-primary/10 text-primary' aria-hidden='true'>
                    <Settings className='size-6' />
                </span>

                <div>
                    <h1 className='text-2xl leading-tight font-bold tracking-tight text-foreground sm:text-[1.75rem]'>
                        Pengaturan
                    </h1>

                    <p className='mt-0.5 text-sm text-muted-foreground'>
                        Atur identitas aplikasi dan akun pengguna.
                    </p>
                </div>
            </div>

            <BrandingSection />

            {currentUser.role === 'admin' && <UsersSection />}
        </>
    )
}

export default SettingsPage
